/**
 * AdminKartoteki — widok „Kartoteki" w panelu super-admina (ITE).
 *
 * Jeden rejestr wszystkich kartotek platformy. Główny podział = ŹRÓDŁO:
 *  - systemowa — stworzona przez ITE (hardcoded/seed); edycja w kolejnym etapie.
 *  - kreator   — encja szablonu Kreatora; edycja przez edytor szablonu.
 *
 * Tabela renderowana przez `ConfigurableTable` (ten sam silnik listy co u klienta).
 * ConfigurableTable używa `useTenantAuth`, którego w scope admina NIE ma — dlatego
 * owijamy w `PreviewTenantAuthBoundary` (atrapa pustej sesji, bez sieci) i podajemy
 * `persistConfig={false}` (żadnych zapytań o layout tenanta).
 *
 * Kolumna „Używana w modułach" pokazuje chipy (Badge) — moduły-szablony, które
 * używają danej kartoteki (relacja liczona po stronie backendu, pole `usedInModules`).
 *
 * Kartoteka „Dokumenty" grupuje 7 klas dokumentów w ROZWIJALNY wiersz (chevron).
 * ConfigurableTable nie ma natywnych wierszy potomnych, więc odwzorowujemy zagnieżdżenie
 * wstrzykując wiersze-klasy do płaskiej tablicy `data` tuż po wierszu-grupie, gdy jest
 * rozwinięty (stan `expandedKeys`). Sortowanie kolumn wyłączone, by zachować sąsiedztwo
 * grupa→klasy. Każda klasa ma inline akcje „Edytuj pola" i „Duplikuj" (jak w oryginale).
 *
 * Endpoint: GET /api/admin/kartoteki.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { DocumentClass } from '@saas/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { ChevronDown, ChevronRight, Copy, Database, FilePlus2, Plus, Trash2 } from 'lucide-react';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import {
  type ColumnDefinition,
  type ColumnFilters,
  ConfigurableTable,
} from '@/components/configurable-table';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import {
  DocumentTypeEditorModal,
  type DocumentTypeEditorTarget,
} from '@/components/documents/DocumentTypeEditorModal';
import {
  emptyGlobalDocumentTypeFormValues,
  GlobalDocumentTypeFormModal,
  type GlobalDocumentTypeFormValues,
} from '@/components/documents/GlobalDocumentTypeFormModal';
import { Dropdown } from '@/components/Dropdown';
import { IconButton } from '@/components/IconButton';
import { Input } from '@/components/Input';
import {
  type CreatedKartoteka,
  type CreateKartotekaInput,
  CreateKartotekaModal,
} from '@/components/kartoteki/CreateKartotekaModal';
import {
  CreatorKartotekaFieldsModal,
  type CreatorKartotekaTarget,
} from '@/components/kartoteki/CreatorKartotekaFieldsModal';
import { KartotekaFieldsManager } from '@/components/kartoteki/KartotekaFieldsManager';
import { Layout } from '@/components/Layout';
import { Modal } from '@/components/Modal';
import { Skeleton } from '@/components/Skeleton';
import { useAuth } from '@/contexts/AuthContext';
import { PreviewTenantAuthBoundary } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

/** Źródło duplikacji — kreatorowa encja albo kartoteka systemowa (SEED_MODULES). */
type DuplicateSource =
  | { source: 'creator'; templateCode: string; entitySlug: string }
  | { source: 'system'; slug: string };

/** Kartoteka „Z Kreatora" wybrana do usunięcia (miękkiego). */
interface PendingDeleteKartoteka {
  templateCode: string;
  entitySlug: string;
  name: string;
}

// ---------------------------------------------------------------------------
// Typy kontraktu endpointu
// ---------------------------------------------------------------------------

interface KartotekaField {
  name: string;
}

/** Moduł (szablon Kreatora), który używa danej kartoteki. */
interface UsedInModule {
  code: string;
  name: string;
}

/** Status systemowego typu dokumentu widoczny w panelu ITE (wycofane nie są zwracane). */
type DocTypeStatus = 'draft' | 'published';

/** Systemowy typ dokumentu (globalny) należący do klasy — np. Delegacja. */
interface DocType {
  id: string;
  code: string;
  name: string;
  /** Może być pominięte przez starszy backend — wtedy traktujemy jak opublikowany (brak badge). */
  status?: DocTypeStatus;
}

interface DocClass {
  name: string;
  slug: string;
  /** Kanoniczna klasa dokumentu — cel akcji „Dodaj typ". Pominięta przez starszy backend. */
  documentClass?: DocumentClass | null;
  fieldCount: number;
  fields: KartotekaField[];
  usedInModules: UsedInModule[];
  /** Systemowe typy dokumentów tej klasy (3. poziom drzewa). Może być pominięte przez starszy backend. */
  types?: DocType[];
}

type KartotekaSource = 'systemowa' | 'kreator';

/** Autor kartoteki kreatorowej (autor szablonu). Systemowe → null (brak twórcy, z kodu). */
interface KartotekaAuthor {
  createdByName: string | null;
  updatedByName: string | null;
  updatedAt: string | null;
}

interface KartotekaItem {
  name: string;
  /** Krótki opis kartoteki (kolumna „Opis"). Brak → null. */
  description: string | null;
  /** Slug kartoteki systemowej — pozwala zarządzać polami wprost (E2.2c). */
  slug?: string;
  source: KartotekaSource;
  division: 'kategorie' | 'klasy';
  fieldCount: number;
  fields: KartotekaField[];
  classes?: DocClass[];
  editable: boolean;
  editTarget: { templateCode: string; entitySlug: string } | null;
  usedInModules: UsedInModule[];
  author: KartotekaAuthor | null;
}

/** Formatuje ISO → czytelna data pl-PL. Pusty string przy braku/błędnej dacie. */
function formatDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('pl-PL');
}

interface KartotekiResponse {
  kartoteki: KartotekaItem[];
}

// ---------------------------------------------------------------------------
// Typy wierszy renderowanych w tabeli
//
// `KartotekaRow` — wiersz najwyższego poziomu (kartoteka). `ClassRow` — wiersz-klasa
// (podwiersz grupy „Dokumenty"). ConfigurableTable renderuje je płasko; zagnieżdżenie
// odwzorowuje kolejność w `data` + wcięcie/chevron w komórce „Nazwa".
// ---------------------------------------------------------------------------

type KartotekaRow = KartotekaItem & { _kind: 'kartoteka'; _key: string };

interface ClassRow {
  _kind: 'class';
  _key: string;
  _parentKey: string;
  name: string;
  slug: string;
  /** Kanoniczna klasa dokumentu — cel akcji „Dodaj typ" (preset klasy w modalu). */
  documentClass: DocumentClass | null;
  fieldCount: number;
  usedInModules: UsedInModule[];
  /** Typy dokumentów tej klasy — 3. poziom rozwijany pod wierszem-klasą. */
  types: DocType[];
}

interface TypeRow {
  _kind: 'type';
  _key: string;
  _parentKey: string;
  name: string;
  code: string;
  /** Status typu — steruje badge'em „szkic" na wierszu (published bez badge). */
  _status?: DocTypeStatus;
  /** Id systemowego typu dokumentu — „Edytuj" typu otwiera modal PÓL TEGO TYPU. */
  _typeId: string;
  /** Slug + nazwa klasy-rodzica — kartoteka bazowa pól (slug klasy, apply na poziomie klasy). */
  _classSlug: string;
  _className: string;
}

type DisplayRow = KartotekaRow | ClassRow | TypeRow;

type SourceFilter = 'all' | KartotekaSource;

// ---------------------------------------------------------------------------
// Budowniczowie podwierszy (klasa / typ) — wspólne dla trybu drzewka
// (rozwijanie po `expandedKeys`) i trybu wyszukiwania (płaskie dopasowania).
// ---------------------------------------------------------------------------

function buildClassRow(parentKey: string, cls: DocClass): ClassRow {
  return {
    _kind: 'class',
    _key: `${parentKey}-class-${cls.slug}`,
    _parentKey: parentKey,
    name: cls.name,
    slug: cls.slug,
    documentClass: cls.documentClass ?? null,
    fieldCount: cls.fieldCount,
    usedInModules: cls.usedInModules,
    types: cls.types ?? [],
  };
}

function buildTypeRow(
  classKey: string,
  classSlug: string,
  className: string,
  ty: DocType,
): TypeRow {
  return {
    _kind: 'type',
    _key: `${classKey}-type-${ty.id}`,
    _parentKey: classKey,
    name: ty.name,
    code: ty.code,
    _status: ty.status,
    _typeId: ty.id,
    _classSlug: classSlug,
    _className: className,
  };
}

/**
 * Tekst danego wiersza dla kolumny — źródło dopasowania wyszukiwarki per-kolumna.
 * Zwraca pusty string dla par (wiersz, kolumna), które nie niosą wartości
 * (np. „Używana w modułach" dla wiersza-typu) — taki wiersz nigdy nie dopasuje
 * się do niepustej frazy, ale zostanie pokazany jako przodek dopasowanego dziecka.
 */
function rowSearchValue(row: DisplayRow, colId: string): string {
  switch (colId) {
    case 'name':
      return row.name;
    case 'usedInModules':
      if (row._kind === 'type') return '';
      return row.usedInModules.map((m) => `${m.name} ${m.code}`).join(' ');
    default:
      return '';
  }
}

/**
 * Cel modala zarządzania polami — kartoteka systemowa, klasa dokumentu, LUB typ dokumentu.
 * `typeId` ustawione → modal PÓL TYPU (dwie sekcje: odziedziczone z klasy + własne typu);
 * `slug` to wtedy slug klasy-rodzica (kartoteka bazowa pól).
 */
interface ManageTarget {
  slug: string;
  name: string;
  typeId?: string;
  typeName?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AdminKartoteki() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();

  // Zarządzanie globalnymi typami dokumentów (tworzenie szkicu przez „Dodaj typ") gatowane
  // tym samym uprawnieniem co ekran „Globalne typy dokumentów".
  const canManageTypes = hasPermission('global_document_types.manage');

  const [previewRow, setPreviewRow] = useState<KartotekaRow | null>(null);
  const [manageTarget, setManageTarget] = useState<ManageTarget | null>(null);
  // Wiersz typu → edytor typu z zakładkami „Ustawienia" + „Pola" (ADR-0085, E2.5).
  const [editorTarget, setEditorTarget] = useState<DocumentTypeEditorTarget | null>(null);
  // Kartoteka „Z Kreatora" otwarta do edycji pól w miejscu (modal, bez skoku do edytora).
  const [creatorTarget, setCreatorTarget] = useState<CreatorKartotekaTarget | null>(null);
  const [searchPhrase, setSearchPhrase] = useState('');
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  // Wyszukiwarka per-kolumna („Szukaj…" pod nagłówkami) — sterowana z zewnątrz,
  // więc ConfigurableTable renderuje inputy, ale filtrowanie robimy sami niżej
  // (przez `displayRows`), z zachowaniem przodków w drzewku (bez sierot).
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>({});
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const [pendingDelete, setPendingDelete] = useState<PendingDeleteKartoteka | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  // Klasa dokumentu, w której tworzymy nowy systemowy typ („Dodaj typ" na wierszu klasy).
  const [addTypeClass, setAddTypeClass] = useState<{
    documentClass: DocumentClass;
    slug: string;
    name: string;
  } | null>(null);
  const [addTypeErrors, setAddTypeErrors] = useState<Record<string, string>>({});
  // MEMOIZOWANE (code-review debt, obszar numeracji, F20) — `emptyGlobalDocumentTypeFormValues()`
  // wołane inline w JSX tworzyłoby NOWY obiekt przy KAŻDYM renderze tej (bardzo ruchliwej)
  // strony, podczas gdy modal jest zamontowany. `GlobalDocumentTypeFormModal` resetuje stan
  // formularza w efekcie zależnym od `initialValues` (real deps) — niestabilna referencja
  // odpalałaby ten reset przy każdym nieistotnym re-renderze, wycierając niezapisaną edycję.
  // Kształt jest zawsze ten sam (create-mode, brak dynamicznych danych) — jedna instancja
  // na cały czas życia komponentu.
  const emptyAddTypeValues = useMemo(() => emptyGlobalDocumentTypeFormValues(), []);

  const query = useQuery<KartotekiResponse>({
    queryKey: ['admin', 'kartoteki'],
    queryFn: async () => (await api.get<KartotekiResponse>('/admin/kartoteki')).data,
    retry: false,
  });

  // ── Duplikacja (kreatorowa + systemowa → nowy szablon z jedną kartoteką) ──────
  const duplicateMutation = useMutation({
    mutationFn: async (payload: DuplicateSource) => {
      const res = await api.post<{ templateCode: string; entitySlug: string }>(
        '/admin/kartoteki/duplicate',
        payload,
      );
      return res.data;
    },
    onSuccess: (data) => {
      toast.show({ variant: 'success', title: t('adminKartoteki.toasts.duplicated') });
      queryClient.invalidateQueries({ queryKey: ['admin', 'kartoteki'] });
      // Otwórz edytor nowej kartoteki (jak przy tworzeniu szablonu).
      navigate(`/module-templates/${data.templateCode}`);
    },
    onError: () =>
      toast.show({ variant: 'error', title: t('adminKartoteki.toasts.duplicateError') }),
  });

  // ── Usuwanie kreatorowej (miękkie; ostatnia w module → kasuje pusty moduł) ────
  const deleteMutation = useMutation({
    mutationFn: async (payload: { templateCode: string; entitySlug: string }) => {
      await api.post('/admin/kartoteki/delete-creator', payload);
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('adminKartoteki.toasts.deleted') });
      queryClient.invalidateQueries({ queryKey: ['admin', 'kartoteki'] });
      setPendingDelete(null);
    },
    onError: () => {
      toast.show({ variant: 'error', title: t('adminKartoteki.toasts.deleteError') });
      setPendingDelete(null);
    },
  });

  // ── Tworzenie systemowego typu dokumentu (szkic) w wybranej klasie („Dodaj typ") ──
  // Reużywa endpointu ekranu „Globalne typy dokumentów" (tworzy status='draft'). Po sukcesie
  // odświeżamy drzewko kartotek — świeży szkic jest w nim od razu widoczny (badge „szkic").
  const createTypeMutation = useMutation({
    mutationFn: async ({
      documentClass,
      values,
    }: {
      documentClass: DocumentClass;
      values: GlobalDocumentTypeFormValues;
    }) => {
      const body: Record<string, unknown> = {
        class: documentClass,
        code: values.code,
        name: values.name,
      };
      if (values.description.trim()) body.description = values.description.trim();
      if (values.numberingFormat.trim()) body.numberingFormat = values.numberingFormat.trim();
      if (values.numberingBaseField) body.numberingBaseField = values.numberingBaseField;
      body.numberingReset = values.numberingReset;
      if (values.sortOrder.trim() !== '') body.sortOrder = parseInt(values.sortOrder, 10);
      if (values.formViewSlug) body.formViewSlug = values.formViewSlug;
      if (values.defaultProcessTemplateId)
        body.defaultProcessTemplateId = values.defaultProcessTemplateId;
      await api.post('/admin/global-document-types', body);
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('adminDocumentTypes.toast.created') });
      queryClient.invalidateQueries({ queryKey: ['admin', 'kartoteki'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'global-document-types'] });
      setAddTypeClass(null);
      setAddTypeErrors({});
    },
    onError: (err) => {
      const code = isAxiosError(err)
        ? (err.response?.data?.error as string | undefined)
        : undefined;
      if (code === 'CODE_TAKEN') {
        setAddTypeErrors({ code: t('adminDocumentTypes.toast.codeTaken') });
        return;
      }
      if (code === 'NAME_TAKEN') {
        setAddTypeErrors({ name: t('adminDocumentTypes.toast.nameTaken') });
        return;
      }
      toast.show({ variant: 'error', title: t('adminDocumentTypes.toast.createError') });
    },
  });

  const allRows: KartotekaRow[] = useMemo(
    () =>
      query.data
        ? query.data.kartoteki.map(
            (k): KartotekaRow => ({
              ...k,
              _kind: 'kartoteka',
              // Defensywnie — starszy backend mógłby pominąć pole relacji.
              usedInModules: k.usedInModules ?? [],
              _key: k.editTarget
                ? `edit-${k.editTarget.templateCode}-${k.editTarget.entitySlug}`
                : `sys-${k.name}`,
            }),
          )
        : [],
    [query.data],
  );

  // Slugi już zajęte (kartoteki systemowe + klasy dokumentów + encje Kreatora) — kliencka
  // podpowiedź „już zajęty" w modalu tworzenia; backend ma ostatnie słowo.
  const existingSlugs = useMemo(
    () =>
      allRows.flatMap((r) => [
        ...(r.editTarget ? [r.editTarget.entitySlug] : []),
        ...(r.slug ? [r.slug] : []),
        ...(r.classes?.map((c) => c.slug) ?? []),
      ]),
    [allRows],
  );

  // Nazwy zajęte (kartoteki systemowe + Kreatora) — kliencka podpowiedź „nazwa zajęta"
  // w modalu tworzenia; backend egzekwuje globalnie (409 NAME_TAKEN).
  const existingNames = useMemo(() => allRows.map((r) => r.name), [allRows]);

  // Zapis nowej kartoteki (admin) — kartoteka rodzi się w samodzielnym szablonie-nośniku,
  // adres = kod tego szablonu (niesiony w polu `id`), dalej redirect do edytora.
  const createKartoteka = async (input: CreateKartotekaInput): Promise<CreatedKartoteka> => {
    const res = await api.post<{ templateCode: string; entitySlug: string }>(
      '/admin/kartoteki/create',
      input,
    );
    return { id: res.data.templateCode, slug: res.data.entitySlug, name: input.name };
  };

  const handleCreated = (result: CreatedKartoteka) => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'kartoteki'] });
    if (result.id) navigate(`/module-templates/${result.id}`);
  };

  // Filtr źródła zawęża listę kartotek najwyższego poziomu (jak dotychczas).
  // Wyszukiwarki tekstowe (górna fraza + per-kolumna) aplikujemy osobno w
  // `displayRows` — bo muszą schodzić w głąb drzewka i ciągnąć przodków.
  const rows: KartotekaRow[] = useMemo(() => {
    if (sourceFilter === 'all') return allRows;
    return allRows.filter((row) => row.source === sourceFilter);
  }, [allRows, sourceFilter]);

  // Aktywne ograniczenia tekstowe: górna fraza traktowana jak filtr kolumny
  // „Nazwa", plus każda niepusta „Szukaj…" per-kolumna. Wszystkie łączone AND.
  const searchConstraints = useMemo(() => {
    const list: { colId: string; val: string }[] = [];
    const phrase = searchPhrase.trim().toLowerCase();
    if (phrase) list.push({ colId: 'name', val: phrase });
    for (const [colId, val] of Object.entries(columnFilters)) {
      if (val != null && String(val).trim() !== '') {
        list.push({ colId, val: String(val).trim().toLowerCase() });
      }
    }
    return list;
  }, [searchPhrase, columnFilters]);

  const isFiltering = searchConstraints.length > 0;

  function toggleExpand(key: string) {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  // Płaska lista wierszy dla ConfigurableTable — silnik nie ma wierszy potomnych,
  // więc zagnieżdżenie odwzorowujemy kolejnością w tablicy.
  //
  // Tryb DRZEWKA (brak filtra tekstowego): po każdym ROZWINIĘTYM wierszu-grupie
  // („Dokumenty") wstrzykujemy jego klasy, a po rozwiniętej klasie — jej typy.
  //
  // Tryb WYSZUKIWANIA (aktywna fraza / „Szukaj…" per-kolumna): pokazujemy PŁASKO
  // wszystkie dopasowane wiersze BEZ wymogu rozwijania, zachowując przodków —
  // dopasowany typ ciągnie swoją klasę i kartotekę, więc nie ma sierot.
  const displayRows: DisplayRow[] = useMemo(() => {
    const out: DisplayRow[] = [];

    if (isFiltering) {
      const matches = (row: DisplayRow) =>
        searchConstraints.every((c) => rowSearchValue(row, c.colId).toLowerCase().includes(c.val));
      for (const row of rows) {
        const included: { classRow: ClassRow; types: TypeRow[] }[] = [];
        for (const cls of row.classes ?? []) {
          const classRow = buildClassRow(row._key, cls);
          const matchedTypes = (cls.types ?? [])
            .map((ty) => buildTypeRow(classRow._key, cls.slug, cls.name, ty))
            .filter(matches);
          if (matches(classRow) || matchedTypes.length > 0) {
            included.push({ classRow, types: matchedTypes });
          }
        }
        if (matches(row) || included.length > 0) {
          out.push(row);
          for (const { classRow, types } of included) {
            out.push(classRow);
            out.push(...types);
          }
        }
      }
      return out;
    }

    for (const row of rows) {
      out.push(row);
      const classes = row.classes ?? [];
      if (classes.length > 0 && expandedKeys.has(row._key)) {
        for (const cls of classes) {
          const classRow = buildClassRow(row._key, cls);
          out.push(classRow);
          // 3. poziom: typy dokumentów klasy, rozwijane osobnym kluczem `docclass-<slug>`.
          if (classRow.types.length > 0 && expandedKeys.has(`docclass-${cls.slug}`)) {
            for (const ty of classRow.types) {
              out.push(buildTypeRow(classRow._key, cls.slug, cls.name, ty));
            }
          }
        }
      }
    }
    return out;
  }, [rows, expandedKeys, isFiltering, searchConstraints]);

  // Kolumny ConfigurableTable — sortowanie WYŁĄCZONE (`sortable: false`), by nie rozerwać
  // sąsiedztwa grupa→klasy po wstrzyknięciu podwierszy. Render przez custom cells.
  const columns: ColumnDefinition<DisplayRow>[] = useMemo(
    () => [
      {
        id: 'name',
        header: t('adminKartoteki.columns.name'),
        accessor: (row) => row.name,
        sortable: false,
        defaultWidth: 280,
        renderCell: (_v, row) => {
          if (row._kind === 'type') {
            return (
              <span
                className="flex items-center gap-2 pl-28 text-text-secondary"
                data-testid={`type-row-${row._parentKey}-${row.code}`}
              >
                {row.name}
                <Badge color="gray">{row.code}</Badge>
                {row._status === 'draft' && (
                  <Badge color="amber" data-testid={`type-status-draft-${row.code}`}>
                    {t('adminKartoteki.typeStatus.draft')}
                  </Badge>
                )}
              </span>
            );
          }
          if (row._kind === 'class') {
            const typeCount = row.types.length;
            // W trybie wyszukiwania drzewko jest spłaszczone — chevron zbędny,
            // renderujemy statyczną nazwę (z licznikiem typów, jeśli są).
            if (!isFiltering && typeCount > 0) {
              const expanded = expandedKeys.has(`docclass-${row.slug}`);
              return (
                <Button
                  variant="ghost"
                  size="sm"
                  // `!px-1` (important) nadpisuje padding, więc wcięcie klasy robimy MARGINESEM.
                  className="ml-10 !px-1"
                  aria-expanded={expanded}
                  data-testid={`expand-class-${row._parentKey}-${row.slug}`}
                  leftIcon={
                    expanded ? (
                      <ChevronDown className="h-4 w-4 shrink-0" />
                    ) : (
                      <ChevronRight className="h-4 w-4 shrink-0" />
                    )
                  }
                  onClick={() => toggleExpand(`docclass-${row.slug}`)}
                >
                  <span
                    className="text-text-secondary"
                    data-testid={`class-row-${row._parentKey}-${row.slug}`}
                  >
                    {row.name}
                  </span>
                  <span className="ml-1.5 text-xs font-normal text-text-muted">
                    · {t('adminKartoteki.typesCount', { count: typeCount })}
                  </span>
                </Button>
              );
            }
            return (
              <span
                className="pl-14 text-text-secondary"
                data-testid={`class-row-${row._parentKey}-${row.slug}`}
              >
                {row.name}
                {typeCount > 0 && (
                  <span className="ml-1.5 text-xs font-normal text-text-muted">
                    · {t('adminKartoteki.typesCount', { count: typeCount })}
                  </span>
                )}
              </span>
            );
          }
          const classCount = row.classes?.length ?? 0;
          if (!isFiltering && classCount > 0) {
            const expanded = expandedKeys.has(row._key);
            return (
              <Button
                variant="ghost"
                size="sm"
                className="!px-1"
                aria-expanded={expanded}
                data-testid={`expand-kartoteka-${row._key}`}
                leftIcon={
                  expanded ? (
                    <ChevronDown className="h-4 w-4 shrink-0" />
                  ) : (
                    <ChevronRight className="h-4 w-4 shrink-0" />
                  )
                }
                onClick={() => toggleExpand(row._key)}
              >
                <span className="font-medium text-text-primary">{row.name}</span>
                <span className="ml-1.5 text-xs font-normal text-text-muted">
                  · {t('adminKartoteki.classesCount', { count: classCount })}
                </span>
              </Button>
            );
          }
          if (classCount > 0) {
            return (
              <span className="font-medium text-text-primary">
                {row.name}
                <span className="ml-1.5 text-xs font-normal text-text-muted">
                  · {t('adminKartoteki.classesCount', { count: classCount })}
                </span>
              </span>
            );
          }
          return <span className="font-medium text-text-primary">{row.name}</span>;
        },
      },
      {
        id: 'description',
        header: t('adminKartoteki.columns.description'),
        // Opis niosą tylko wiersze-kartoteki; klasy/typy → puste (pokazujemy „—").
        accessor: (row) => (row._kind === 'kartoteka' ? (row.description ?? '') : ''),
        sortable: false,
        filterable: false,
        defaultWidth: 320,
        renderCell: (_v, row) =>
          row._kind === 'kartoteka' && row.description ? (
            <span className="block whitespace-normal break-words text-text-secondary">
              {row.description}
            </span>
          ) : (
            <span className="text-text-muted">—</span>
          ),
      },
      {
        id: 'source',
        header: t('adminKartoteki.columns.source'),
        accessor: (row) =>
          row._kind === 'type' ? '' : row._kind === 'class' ? 'systemowa' : row.source,
        sortable: false,
        // Źródło filtrujemy górnym selektorem — bez dublującego inputu w kolumnie.
        filterable: false,
        defaultWidth: 140,
        renderCell: (_v, row) =>
          row._kind === 'type' ? (
            <span className="text-text-muted">—</span>
          ) : (
            <SourceBadge source={row._kind === 'class' ? 'systemowa' : row.source} />
          ),
      },
      {
        id: 'author',
        header: t('adminKartoteki.columns.author'),
        accessor: () => '',
        sortable: false,
        filterable: false,
        defaultWidth: 200,
        renderCell: (_v, row) =>
          row._kind === 'kartoteka' ? (
            <AuthorCell author={row.author} />
          ) : (
            <span className="text-text-muted">—</span>
          ),
      },
      {
        id: 'usedInModules',
        header: t('adminKartoteki.columns.usedInModules'),
        accessor: (row) => (row._kind === 'type' ? 0 : row.usedInModules.length),
        sortable: false,
        defaultWidth: 260,
        renderCell: (_v, row) =>
          row._kind === 'type' ? (
            <span className="text-text-muted">—</span>
          ) : (
            <UsedInModulesCell
              modules={row.usedInModules}
              empty={t('adminKartoteki.usedInEmpty')}
            />
          ),
      },
      {
        id: 'fieldCount',
        header: t('adminKartoteki.columns.fieldCount'),
        accessor: (row) =>
          row._kind === 'type'
            ? -1
            : row._kind === 'class'
              ? row.fieldCount
              : (row.classes?.length ?? 0) > 0
                ? -1
                : row.fieldCount,
        sortable: false,
        filterable: false,
        align: 'right',
        defaultWidth: 90,
        renderCell: (_v, row) =>
          row._kind === 'type'
            ? '—'
            : row._kind === 'class'
              ? String(row.fieldCount)
              : (row.classes?.length ?? 0) > 0
                ? '—'
                : String(row.fieldCount),
      },
    ],
    [t, expandedKeys, isFiltering],
  );

  // Akcje wiersza — klasa: Edytuj pola + Duplikuj; kartoteka: Edytuj/Zarządzaj/Podgląd
  // + Duplikuj/Usuń. Wiersz-grupa „Dokumenty" nie ma własnych akcji (działa przez klasy).
  const renderRowActions = (row: DisplayRow) => {
    // Wiersz-typ (3. poziom) — „Edytuj" otwiera edytor typu z zakładkami: „Ustawienia typu"
    // (formularz jak w Globalnych typach) + „Pola" (bramkowanie pól z puli klasy, ADR-0085).
    // Na typie NIE dodajemy pól — pola żyją w puli klasy, typ tylko je bramkuje.
    if (row._kind === 'type') {
      return (
        <div className="flex items-center justify-end gap-1">
          <Button
            size="sm"
            variant="secondary"
            data-testid={`edit-type-${row._parentKey}-${row.code}`}
            onClick={() =>
              setEditorTarget({
                slug: row._classSlug,
                typeId: row._typeId,
                typeName: row.name,
                className: row._className,
              })
            }
          >
            {t('adminKartoteki.actionManage')}
          </Button>
        </div>
      );
    }
    if (row._kind === 'class') {
      return (
        <div className="flex items-center justify-end gap-1">
          <Button
            size="sm"
            variant="secondary"
            data-testid={`manage-class-${row._parentKey}-${row.slug}`}
            onClick={() => setManageTarget({ slug: row.slug, name: row.name })}
          >
            {t('adminKartoteki.actionManage')}
          </Button>
          {canManageTypes && row.documentClass && (
            <IconButton
              size="sm"
              variant="ghost"
              aria-label={t('adminKartoteki.actionAddType')}
              title={t('adminKartoteki.actionAddType')}
              data-testid={`add-type-${row._parentKey}-${row.slug}`}
              onClick={() => {
                setAddTypeErrors({});
                setAddTypeClass({
                  documentClass: row.documentClass!,
                  slug: row.slug,
                  name: row.name,
                });
              }}
            >
              <FilePlus2 />
            </IconButton>
          )}
          <IconButton
            size="sm"
            variant="ghost"
            disabled={duplicateMutation.isPending}
            aria-label={t('adminKartoteki.actionDuplicate')}
            title={t('adminKartoteki.actionDuplicate')}
            data-testid={`duplicate-class-${row._parentKey}-${row.slug}`}
            onClick={() => duplicateMutation.mutate({ source: 'system', slug: row.slug })}
          >
            <Copy />
          </IconButton>
        </div>
      );
    }

    const hasClasses = (row.classes?.length ?? 0) > 0;
    // Duplikuj: kreatorowa (encja) albo systemowa (slug). Grupa „Dokumenty" (klasy) → bez.
    const duplicatePayload: DuplicateSource | null =
      row.editable && row.editTarget
        ? {
            source: 'creator',
            templateCode: row.editTarget.templateCode,
            entitySlug: row.editTarget.entitySlug,
          }
        : row.source === 'systemowa' && row.slug && !hasClasses
          ? { source: 'system', slug: row.slug }
          : null;
    const canDelete = row.editable && row.editTarget;

    return (
      <div className="flex items-center justify-end gap-1">
        {row.editable && row.editTarget ? (
          <Button
            size="sm"
            variant="secondary"
            data-testid={`edit-kartoteka-${row._key}`}
            onClick={() =>
              setCreatorTarget({
                templateCode: row.editTarget!.templateCode,
                entitySlug: row.editTarget!.entitySlug,
                name: row.name,
              })
            }
          >
            {t('adminKartoteki.actionEdit')}
          </Button>
        ) : row.source === 'systemowa' && row.slug ? (
          <Button
            size="sm"
            variant="secondary"
            data-testid={`manage-kartoteka-${row._key}`}
            onClick={() => setManageTarget({ slug: row.slug!, name: row.name })}
          >
            {t('adminKartoteki.actionManage')}
          </Button>
        ) : hasClasses ? null : (
          <Button
            size="sm"
            variant="ghost"
            data-testid={`preview-kartoteka-${row._key}`}
            onClick={() => setPreviewRow(row)}
          >
            {t('adminKartoteki.actionPreview')}
          </Button>
        )}
        {duplicatePayload && (
          <IconButton
            size="sm"
            variant="ghost"
            disabled={duplicateMutation.isPending}
            aria-label={t('adminKartoteki.actionDuplicate')}
            title={t('adminKartoteki.actionDuplicate')}
            data-testid={`duplicate-kartoteka-${row._key}`}
            onClick={() => duplicateMutation.mutate(duplicatePayload)}
          >
            <Copy />
          </IconButton>
        )}
        {canDelete && row.editTarget && (
          <IconButton
            size="sm"
            variant="danger"
            aria-label={t('adminKartoteki.actionDelete')}
            title={t('adminKartoteki.actionDelete')}
            data-testid={`delete-kartoteka-${row._key}`}
            onClick={() =>
              setPendingDelete({
                templateCode: row.editTarget!.templateCode,
                entitySlug: row.editTarget!.entitySlug,
                name: row.name,
              })
            }
          >
            <Trash2 />
          </IconButton>
        )}
      </div>
    );
  };

  return (
    <Layout>
      <section className="px-6 py-8">
        {/* Nagłówek strony */}
        <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">{t('adminKartoteki.title')}</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('adminKartoteki.subtitle')}
            </p>
          </div>
          <Button
            variant="primary"
            leftIcon={<Plus className="h-4 w-4" />}
            data-testid="kartoteki-new-btn"
            onClick={() => setCreateOpen(true)}
          >
            {t('adminKartoteki.newKartoteka')}
          </Button>
        </div>

        {/* Pasek filtrowania */}
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <Input
            data-testid="kartoteki-search"
            placeholder={t('adminKartoteki.searchPlaceholder')}
            value={searchPhrase}
            onChange={(e) => setSearchPhrase(e.target.value)}
            containerClassName="w-64"
          />
          <Dropdown
            data-testid="kartoteki-filter-source"
            value={sourceFilter}
            onChange={(v) => setSourceFilter(v as SourceFilter)}
            className="w-52"
            options={[
              { value: 'all', label: t('adminKartoteki.filterSource.all') },
              { value: 'systemowa', label: t('adminKartoteki.filterSource.systemowa') },
              { value: 'kreator', label: t('adminKartoteki.filterSource.kreator') },
            ]}
          />
        </div>

        {/* Stan błędu */}
        {query.isError && (
          <div
            role="alert"
            className="rounded border border-red-300 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300"
          >
            {t('adminKartoteki.loadError')}
          </div>
        )}

        {/* Ładowanie */}
        {query.isLoading && <Skeleton className="h-40 w-full" />}

        {/* Tabela — ConfigurableTable owinięta w boundary (scope admina bez TenantAuthProvider).
            `filterable` włącza wiersz „Szukaj…" pod nagłówkami; filtry sterowane z zewnątrz
            (`columnFilters`), więc silnik ich sam nie aplikuje — robimy to w `displayRows`.
            Renderuje się zawsze (nawet gdy displayRows jest puste) — nagłówki i wiersz
            filtrów mają zostać widoczne; brak danych / brak wyników filtra pokazuje
            wbudowany placeholder (emptyTitle/emptyAction) zamiast chować całą tabelę. */}
        {!query.isLoading && !query.isError && (
          <PreviewTenantAuthBoundary>
            <ConfigurableTable<DisplayRow>
              tableId="admin-kartoteki"
              columns={columns}
              data={displayRows}
              getRowId={(row) => row._key}
              renderActions={renderRowActions}
              actionsColumnLabel={t('adminKartoteki.columns.actions')}
              actionsColumnWidth={200}
              persistConfig={false}
              configurable={false}
              filterable
              columnFilters={columnFilters}
              onColumnFiltersChange={setColumnFilters}
              onClearAllFilters={() => {
                setSearchPhrase('');
                setSourceFilter('all');
              }}
              showViewModeToggle={false}
              stickyHeader={false}
              testIdPrefix="kartoteka"
              emptyIcon={Database}
              emptyTitle={
                allRows.length === 0
                  ? t('adminKartoteki.empty')
                  : t('adminKartoteki.noFilterResults')
              }
              emptyDescription=""
              emptyAction={
                allRows.length === 0 ? undefined : (
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid="kartoteki-clear-filters"
                    onClick={() => {
                      setSearchPhrase('');
                      setSourceFilter('all');
                      setColumnFilters({});
                    }}
                  >
                    {t('adminKartoteki.clearFilters')}
                  </Button>
                )
              }
            />
          </PreviewTenantAuthBoundary>
        )}
      </section>

      {/* Tworzenie nowej kartoteki (wspólny modal tenant/admin) */}
      <CreateKartotekaModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        submitFn={createKartoteka}
        onCreated={handleCreated}
        existingSlugs={existingSlugs}
        existingNames={existingNames}
      />

      {/* Modal podglądu pól (kartoteki bez sluga — fallback) */}
      <FieldsPreviewModal row={previewRow} onClose={() => setPreviewRow(null)} />

      {/* Modal zarządzania polami — kartoteka systemowa LUB klasa dokumentu (E2.2c) */}
      <ManageFieldsModal target={manageTarget} onClose={() => setManageTarget(null)} />
      <DocumentTypeEditorModal target={editorTarget} onClose={() => setEditorTarget(null)} />

      {/* Modal edycji pól/layoutów/widoków kartoteki „Z Kreatora" (w miejscu, bez skoku do edytora) */}
      <CreatorKartotekaFieldsModal target={creatorTarget} onClose={() => setCreatorTarget(null)} />

      {/* „Dodaj typ" — tworzenie systemowego typu dokumentu (szkic) w konkretnej klasie.
          Klasa jest ustalona z wiersza (preset) i nieedytowalna w modalu. */}
      {addTypeClass && (
        <GlobalDocumentTypeFormModal
          open
          mode="create"
          documentClass={addTypeClass.documentClass}
          initialValues={emptyAddTypeValues}
          saving={createTypeMutation.isPending}
          serverErrors={addTypeErrors}
          onSubmit={(values) => {
            setAddTypeErrors({});
            createTypeMutation.mutate({ documentClass: addTypeClass.documentClass, values });
          }}
          onClose={() => {
            setAddTypeClass(null);
            setAddTypeErrors({});
          }}
        />
      )}

      {/* Usuwanie kreatorowej kartoteki (miękkie, odwracalne) */}
      <ConfirmDialog
        open={pendingDelete !== null}
        variant="danger"
        title={t('adminKartoteki.delete.title')}
        message={
          pendingDelete
            ? `${t('adminKartoteki.delete.confirm', { name: pendingDelete.name })} ${t('adminKartoteki.delete.reversible')}`
            : ''
        }
        confirmLabel={t('adminKartoteki.actionDelete')}
        confirming={deleteMutation.isPending}
        onConfirm={() => {
          if (pendingDelete) {
            deleteMutation.mutate({
              templateCode: pendingDelete.templateCode,
              entitySlug: pendingDelete.entitySlug,
            });
          }
        }}
        onClose={() => setPendingDelete(null)}
      />
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// Chipy „Używana w modułach" — jeden Badge per moduł, zawijane przy wielu.
// ---------------------------------------------------------------------------

function UsedInModulesCell({ modules, empty }: { modules: UsedInModule[]; empty: string }) {
  if (modules.length === 0) {
    return <span className="text-text-muted">{empty}</span>;
  }
  return (
    <div className="flex flex-wrap gap-1 whitespace-normal">
      {modules.map((m) => (
        <Badge key={m.code} color="accent" title={m.code} data-testid="used-in-module-chip">
          {m.name}
        </Badge>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal zarządzania polami (E2.2c) — kartoteka systemowa lub klasa dokumentu.
//
// Reużywa KartotekaFieldsManager — ten sam panel co w edytorze szablonu:
// lista pól (rdzeń + dodane) i „Dodaj pole" (add-only).
// ---------------------------------------------------------------------------

function ManageFieldsModal({
  target,
  onClose,
}: {
  target: ManageTarget | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  const isTypeMode = Boolean(target?.typeId);
  const title = target
    ? isTypeMode
      ? t('adminKartoteki.typeModal.title', { name: target.typeName ?? target.name })
      : t('adminKartoteki.manageModal.title', { name: target.name })
    : '';
  const description = isTypeMode
    ? t('adminKartoteki.typeModal.description')
    : t('adminKartoteki.manageModal.description');

  return (
    <Modal
      open={target !== null}
      onClose={onClose}
      title={title}
      description={description}
      size="2xl"
      fixedHeight
      expandable
      footer={
        <Button variant="secondary" onClick={onClose}>
          {t('adminKartoteki.manageModal.close')}
        </Button>
      }
    >
      {target && (
        <KartotekaFieldsManager
          slug={target.slug}
          typeId={target.typeId}
          // Fleet-push działa na poziomie KLASY (propaguje wszystkie pola). W modalu typu ukryte,
          // by nie sugerować wysyłki „tylko tego typu" — apply zostaje w modalu klasy.
          canApplyToTenants={!isTypeMode}
        />
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Badge źródła
// ---------------------------------------------------------------------------

function SourceBadge({ source }: { source: KartotekaSource }) {
  const { t } = useTranslation();
  return source === 'systemowa' ? (
    <Badge color="green">{t('adminKartoteki.sourceSystemowa')}</Badge>
  ) : (
    <Badge color="blue">{t('adminKartoteki.sourceKreator')}</Badge>
  );
}

// ---------------------------------------------------------------------------
// Komórka „Autor" — twórca + (gdy znany) ostatni edytujący z datą.
// Kartoteki systemowe (z kodu) nie mają twórcy → „—".
// ---------------------------------------------------------------------------

function AuthorCell({ author }: { author: KartotekaAuthor | null }) {
  const { t } = useTranslation();
  if (!author || (!author.createdByName && !author.updatedByName)) {
    return <span className="text-text-muted">—</span>;
  }
  // Brak twórcy (np. szablon seedowany), ale znany edytujący → pokaż edytującego jako głównego.
  const primaryName = author.createdByName ?? author.updatedByName;
  return (
    <div className="flex flex-col">
      <span className="text-text-primary">{primaryName}</span>
      {author.createdByName && author.updatedByName && (
        <span className="text-xs text-text-muted">
          {t('adminKartoteki.editedByShort', {
            name: author.updatedByName,
            date: formatDate(author.updatedAt),
          })}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal podglądu pól kartoteki (nieedytowalnej)
// ---------------------------------------------------------------------------

function FieldsPreviewModal({ row, onClose }: { row: KartotekaRow | null; onClose: () => void }) {
  const { t } = useTranslation();

  return (
    <Modal
      open={row !== null}
      onClose={onClose}
      title={row ? t('adminKartoteki.previewModal.title', { name: row.name }) : ''}
      footer={
        <Button variant="secondary" onClick={onClose}>
          {t('adminKartoteki.previewModal.close')}
        </Button>
      }
    >
      {row && (
        <div className="flex flex-col gap-3">
          {row.fields.length === 0 ? (
            <p className="text-text-muted">{t('adminKartoteki.previewModal.noFields')}</p>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {row.fields.map((f) => (
                <li
                  key={f.name}
                  className="py-1.5 text-sm text-text-primary"
                  data-testid="preview-field-item"
                >
                  {f.name}
                </li>
              ))}
            </ul>
          )}
          <p className="text-xs text-text-muted italic">
            {t('adminKartoteki.previewModal.stage2Note')}
          </p>
        </div>
      )}
    </Modal>
  );
}
